import { useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import type { DebouncedFunc } from 'lodash';

/**
 * Debounced version of `fn` that is stable across renders and always calls the latest `fn`.
 */
const useDebounce = <T extends (...args: never[]) => void>(
  fn: T,
  ms: number,
  maxWait?: number,
): DebouncedFunc<(...args: Parameters<T>) => void> => {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  return useMemo(
    () => debounce(
      (...args: Parameters<T>) => fnRef.current(...args),
      ms,
      maxWait != null ? { maxWait } : {},
    ),
    [ms, maxWait],
  );
};

export default useDebounce;
